"use client"

import { ChevronDown } from "lucide-react"

interface ShowMoreButtonProps {
  /** Handler fired when the user clicks the button */
  onClick: () => void
  /** Button label shown above the chevron (default: "Show more") */
  label?: string
  /** Disable the button */
  disabled?: boolean
  /** Additional classes on the outer wrapper */
  className?: string
}

/**
 * ShowMoreButton — stacked two-line pagination control:
 *  - top: uppercase wide-tracking label (default "Show more")
 *  - bottom: circular chevron-down icon with gray border
 *
 * Hovering turns the border and chevron orange and lifts the chevron 2px.
 * Use below any paginated grid/list to reveal more items.
 */
export function ShowMoreButton({
  onClick,
  label = "Show more",
  disabled = false,
  className = "",
}: ShowMoreButtonProps) {
  return (
    <div className={`flex justify-center ${className}`}>
      <button
        type="button"
        onClick={onClick}
        disabled={disabled}
        className="group inline-flex flex-col items-center gap-2 text-gray-700 hover:text-[#ff9951] transition-colors disabled:opacity-40 disabled:hover:text-gray-700 disabled:cursor-not-allowed"
      >
        <span className="text-[12px] sm:text-[13px] font-semibold uppercase tracking-[0.12em] sm:tracking-[0.15em]">{label}</span>
        <span className="inline-flex h-8 w-8 sm:h-10 sm:w-10 items-center justify-center rounded-full border border-gray-300 group-hover:border-[#ff9951] group-hover:bg-[#ff9951]/[0.06] transition-colors">
          <ChevronDown className="w-4 h-4 sm:w-5 sm:h-5 transition-transform group-hover:translate-y-0.5" />
        </span>
      </button>
    </div>
  )
}
